/**
 * 列表页五态区块（03-UIUX.md §5.1）。
 *
 * 「首次无数据」与「筛选无结果」是**两个不同组件**，不是同一个组件的两个参数 ——
 * 规范明确要求筛选空态里不得出现「创建」按钮。做成参数，早晚有人把它传错。
 */
import type { ReactNode } from 'react';
import { Icon } from '../icons/Icon';
import type { IconName } from '../icons/registry';
import { ICON_SIZE } from '../tokens';

/** Loading：骨架行数 = 上次每页条数，行高与真实行一致，避免 CLS */
export function TableSkeleton({ rows, columns }: { rows: number; columns: number }) {
  return (
    <tbody aria-busy="true">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <tr key={rowIndex}>
          {Array.from({ length: columns }, (_, cellIndex) => (
            <td key={cellIndex}>
              <div
                className="skeleton-bar"
                style={{ width: `${[70, 45, 55, 40, 60, 50][cellIndex % 6]}%` }}
              />
            </td>
          ))}
        </tr>
      ))}
    </tbody>
  );
}

export interface EmptyFirstRunProps {
  readonly icon: IconName;
  /** 一句话说明「这个对象是什么、为什么需要它」，禁止占位文案 */
  readonly description: string;
  readonly action?: ReactNode;
}

export function EmptyFirstRun({ icon, description, action }: EmptyFirstRunProps) {
  return (
    <div className="state-block">
      <span className="state-block__icon">
        <Icon name={icon} size={ICON_SIZE.standalone} />
      </span>
      <p className="state-block__body">{description}</p>
      {action === undefined ? null : <div className="state-block__actions">{action}</div>}
    </div>
  );
}

export interface EmptyFilteredProps {
  readonly description: string;
  readonly onClearFilters: () => void;
}

export function EmptyFiltered({ description, onClearFilters }: EmptyFilteredProps) {
  return (
    <div className="state-block">
      <span className="state-block__icon">
        <Icon name="search-x" size={ICON_SIZE.standalone} />
      </span>
      <p className="state-block__body">{description}</p>
      <div className="state-block__actions">
        <button type="button" className="btn" onClick={onClearFilters}>
          <Icon name="filter-x" size={ICON_SIZE.inline} />
          清除全部筛选
        </button>
      </div>
    </div>
  );
}

export interface ErrorBlockProps {
  /** 具体到能定位问题，如「加载授权列表失败：请求超时（30s）」 */
  readonly message: string;
  readonly onRetry: () => void;
}

export function ErrorBlock({ message, onRetry }: ErrorBlockProps) {
  return (
    <div className="state-block" role="alert">
      <span className="state-block__icon state-block__icon--error">
        <Icon name="octagon-x" size={ICON_SIZE.standalone} />
      </span>
      <p className="state-block__title">{message}</p>
      <div className="state-block__actions">
        <button type="button" className="btn" onClick={onRetry}>
          <Icon name="refresh-cw" size={ICON_SIZE.inline} />
          重试
        </button>
      </div>
    </div>
  );
}

/** 卡片区域的加载占位（非表格场景） */
export function CardSkeleton({ lines = 3 }: { lines?: number }) {
  return (
    <div className="card" aria-busy="true">
      {Array.from({ length: lines }, (_, index) => (
        <div
          key={index}
          className="skeleton-bar"
          style={{
            width: `${[60, 85, 45][index % 3]}%`,
            marginTop: index === 0 ? 0 : 'var(--space-3)',
          }}
        />
      ))}
    </div>
  );
}
